import * as React from "react";
import {
  listVerantwortlichkeitMatrix,
  type VerantwortlichkeitMatrixEintrag,
} from "../../services/verantwortlichkeitService";

// Modul-Cache je Rechtsform. Die Matrix ist Stammdatum und ändert sich nur, wenn
// der PO sie pflegt; in der Ansprechpartner-Bereinigung stehen dutzende Karten
// derselben Firma nebeneinander — ohne Cache wären das dutzende gleiche Requests.
const cache = new Map<number, VerantwortlichkeitMatrixEintrag[]>();
const inflight = new Map<number, Promise<VerantwortlichkeitMatrixEintrag[]>>();

/**
 * Lädt die an einer Rechtsform zulässigen Organrollen (gecacht).
 *
 * Ohne `legalFormId` wird gar nicht geladen: Der Aufrufer kennt die Rechtsform
 * dann nicht, und `loading` bliebe sonst dauerhaft hängen.
 */
export function useVerantwortlichkeitMatrix(legalFormId?: number): {
  matrix: VerantwortlichkeitMatrixEintrag[];
  loading: boolean;
} {
  const id = legalFormId && Number.isFinite(legalFormId) && legalFormId > 0 ? legalFormId : undefined;
  const [matrix, setMatrix] = React.useState<VerantwortlichkeitMatrixEintrag[]>(
    () => (id != null ? cache.get(id) ?? [] : []),
  );
  const [loading, setLoading] = React.useState<boolean>(id != null && !cache.has(id));

  React.useEffect(() => {
    if (id == null) {
      setMatrix([]);
      setLoading(false);
      return;
    }
    const treffer = cache.get(id);
    if (treffer) {
      setMatrix(treffer);
      setLoading(false);
      return;
    }
    setLoading(true);
    let laufend = inflight.get(id);
    if (!laufend) {
      laufend = listVerantwortlichkeitMatrix(id)
        .then((d) => {
          cache.set(id, d);
          return d;
        })
        .finally(() => {
          inflight.delete(id);
        });
      inflight.set(id, laufend);
    }
    let alive = true;
    laufend
      .then((d) => {
        if (!alive) return;
        setMatrix(d);
        setLoading(false);
      })
      .catch(() => {
        // Fehlgeschlagen = keine Organrollen. Die Oberfläche zeigt dann denselben
        // Hinweis wie bei fehlender Rechtsform, statt eine halbe Liste anzubieten.
        if (!alive) return;
        setMatrix([]);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  return { matrix, loading };
}

/** Cache leeren (z. B. nachdem die Matrix in den Einstellungen geändert wurde). */
export function invalidateVerantwortlichkeitMatrixCache(): void {
  cache.clear();
}
